<script setup lang="ts">
import PageHeader from '@/components/page-header.vue'
import PageContent from '@/components/page-content.vue'
import ContentLoader from "@/components/content-loader.vue";
import {faDownload, faCircleInfo,faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import ButtonSecondary from '@/components/button-secondary.vue';
import ButtonHighlighted from '@/components/button-highlighted.vue';
import InputSelect from "@/components/input-select.vue";
import { filterQueries, mainQueries } from '@/features/tariffs/queries';
import { useQuery } from '@tanstack/vue-query';
import { computed, reactive, ref, watch } from 'vue';
import Tabs from '@/components/tabs-teal.vue'
import splashImage from '@/assets/tariffs/dashboard/splash.svg'
import HeroSplash from '@/components/hero-splash.vue'
import { Season, ChargeName, PSOShort } from '@/features/tariffs/constants'
import type { FlattenedTariff, Tariffs } from '@/features/tariffs/api/fetchTariffs';
import { faHandPointUp } from '@fortawesome/pro-solid-svg-icons';
import  ModalTouProfile  from './modal-tou-profie.vue';
import { useApproveTariffs } from '@/features/tariffs/mutations';
import { useModalState } from '@/composables/modal';
import useNotifications from '@/composables/notifications';
import { useDebounce } from '@vueuse/core';

const regions = ref<number[]>([])
const utilityProviders = ref<number[]>([])
const tariffIds = ref<number[]>([])
const compareUtilityProviders = ref<string[]>([])
const comparetariffNames = ref<string[]>([])

const { addSuccess, addError } = useNotifications();
const { data: regionData } = useQuery(filterQueries.fetchRegion())
const { data: utilityProviderData } = useQuery(filterQueries.fetchProviders(regions))
const { data: tariffNamesData } = useQuery(filterQueries.fetchTariffNames(utilityProviders))
const { data: yearsData} = useQuery(filterQueries.fetchTariffYears(tariffIds))

const years = computed(() => ['Compare & Approve', ...(yearsData.value?.map(String) ?? [])])
const selectedYear = ref('');
const touProfiles = reactive(useModalState())

watch(
  years,
  (newYears) => {
    if (newYears.length > 0) {
      selectedYear.value = newYears.length > 1 ? newYears[1] : newYears[0];
    }
  },
  { immediate: true }
);
watch(
  regions,
  () => {
    utilityProviders.value=[]
    tariffIds.value=[]
  },
  { immediate: true }
);
watch(
  utilityProviders,
  () => {
    tariffIds.value=[]
  },
  { immediate: true }
);

const debouncedIds = useDebounce(tariffIds, 1000)
const { data: tariffsData, isFetching  } = useQuery(mainQueries.fetchTariffs(debouncedIds, selectedYear))
const { mutateAsync, isPending } = useApproveTariffs(tariffIds, selectedYear)

const flatData = computed(() => {
  const data = flattenTariffs(tariffsData.value ?? [])
  if ((!compareUtilityProviders.value || compareUtilityProviders.value.length === 0) &&
      (!comparetariffNames.value || comparetariffNames.value.length === 0)) {
    return data
  }

  return data.filter(item => {
    const matchesProvider = compareUtilityProviders.value.length === 0 ||
      compareUtilityProviders.value.includes(item.ProviderName)

    const matchesTariff = comparetariffNames.value.length === 0 ||
      comparetariffNames.value.includes(item.TariffName)

    return matchesProvider && matchesTariff
  })
})
const IsCompare = computed(() => selectedYear.value.startsWith('Compare'))
const compareUtilityProviderData = computed(() => {
  if (!flatData.value) return []

  const addedNames = new Set()

  return flatData.value
    .filter(x => {
      if (!x.ProviderName || addedNames.has(x.ProviderName)) return false
      addedNames.add(x.ProviderName)
      return true
    })
    .map(x => ({
      Name: x.ProviderName
    }))
})
const compareTariffNamesData = computed(() => {
  if (!flatData.value) return []

  const addedNames = new Set()

  return flatData.value
    .filter(x => {
      if (!x.TariffName || addedNames.has(x.TariffName)) return false
      addedNames.add(x.TariffName)
      return true
    })
    .map(x => ({
      Name: x.TariffName
    }))
})
function flattenTariffs(tariffs: Tariffs| undefined): FlattenedTariff[] {
  if (!Array.isArray(tariffs)) return [];

  var res= tariffs.map(tariff => {
    const base = {
      TariffId: IsCompare.value ? tariff.ExternalId??0 : tariff.Id,
      ProviderId: tariff.ProviderId,
      ProviderName: tariff.ProviderName,
      TariffName: tariff.Name,
      TransZone: tariff.TransZone,
      Voltage: tariff.Voltage,
      IsTimeOfUse: tariff.IsTimeOfUse,
      IsApproved: tariff.IsApproved
    };

    const charges: Omit<FlattenedTariff, keyof typeof base> = {
      Block: [],
      Rate: [],

      PeakW: [],
      StandardW: [],
      OffPeakW: [],
      PeakS: [],
      StandardS: [],
      OffPeakS: [],

      BasicChargeW: [],
      DemandChargeW: [],
      AccessChargeW: [],
      ServiceChargeW: [],
      CapacityChargeW: [],
      ReactiveChargeW: [],
      LegacyChargeW: [],
      GenerationChargeW: [],
      CreditReverseChargeW: [],

      BasicChargeS: [],
      DemandChargeS: [],
      AccessChargeS: [],
      ServiceChargeS: [],
      CapacityChargeS: [],
      ReactiveChargeS: [],
      LegacyChargeS: [],
      GenerationChargeS: [],
      CreditReverseChargeS: [],
      BandId: [],
      IsActive: [],
      YearOfTariff: 0,
      OldRate: [],
      RateCP: [],
      OldPeakW: [],
      PeakWCP: [],
      OldStandardW: [],
      StandardWCP: [],
      OldOffPeakW: [],
      OffPeakWCP: [],
      OldPeakS: [],
      PeakSCP: [],
      OldStandardS: [],
      StandardSCP: [],
      OldOffPeakS: [],
      OffPeakSCP: [],
      OldBasicChargeW: [],
      BasicChargeWCP: [],
      OldDemandChargeW: [],
      DemandChargeWCP: [],
      OldAccessChargeW: [],
      AccessChargeWCP: [],
      OldServiceChargeW: [],
      ServiceChargeWCP: [],
      OldCapacityChargeW: [],
      CapacityChargeWCP: [],
      OldReactiveChargeW: [],
      ReactiveChargeWCP: [],
      OldCreditReverseChargeW: [],
      CreditReverseChargeWCP: [],
      OldBasicChargeS: [],
      BasicChargeSCP: [],
      OldDemandChargeS: [],
      DemandChargeSCP: [],
      OldAccessChargeS: [],
      AccessChargeSCP: [],
      OldServiceChargeS: [],
      ServiceChargeSCP: [],
      OldCapacityChargeS: [],
      CapacityChargeSCP: [],
      OldReactiveChargeS: [],
      ReactiveChargeSCP: [],
      OldCreditReverseChargeS: [],
      CreditReverseChargeSCP: [],
      OldLegacyChargeW: [],
      LegacyChargeWCP: [],
      OldGenerationChargeW: [],
      GenerationChargeWCP: [],
      OldLegacyChargeS: [],
      LegacyChargeSCP: [],
      OldGenerationChargeS: [],
      GenerationChargeSCP: [],
      ElectrificationChargeW: [],
      OldElectrificationChargeW: [],
      ElectrificationChargeWCP: [],
      AffordabilityChargeW: [],
      OldAffordabilityChargeW: [],
      AffordabilityChargeWCP: [],
      AncillaryChargeW: [],
      OldAncillaryChargeW: [],
      AncillaryChargeWCP: [],
      ElectrificationChargeS: [],
      OldElectrificationChargeS: [],
      ElectrificationChargeSCP: [],
      AffordabilityChargeS: [],
      OldAffordabilityChargeS: [],
      AffordabilityChargeSCP: [],
      AncillaryChargeS: [],
      OldAncillaryChargeS: [],
      AncillaryChargeSCP: []
    };


    for (const band of tariff.Bands) {
      const isWinter = band.Season === Season.Winter;
      const isSummer = band.Season === Season.Summer;
      const isNone = band.Season === Season.None;
      if (band.Name === ChargeName.Energy && !tariff.IsTimeOfUse) {
        charges.Block.push((band.StartOfBand || band.EndOfBand) ? `${band.StartOfBand} - ${band.EndOfBand}` : '');
        charges.Rate.push(band.BandRate);
        charges.OldRate.push(band.OldRate??0);
        charges.RateCP.push(band.RateChangePercent??0);
      }

      if (isWinter || isNone) {
       if (band.Name === ChargeName.Basic){
          charges.BasicChargeW.push(band.BandRate);
          charges.OldBasicChargeW.push(band.OldRate??0);
          charges.BasicChargeWCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Demand) {
          charges.DemandChargeW.push(band.BandRate);
          charges.OldDemandChargeW.push(band.OldRate??0);
          charges.DemandChargeWCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Access) {
          charges.AccessChargeW.push(band.BandRate);
          charges.OldAccessChargeW.push(band.OldRate??0);
          charges.AccessChargeWCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Service) {
          charges.ServiceChargeW.push(band.BandRate);
          charges.OldServiceChargeW.push(band.OldRate??0);
          charges.ServiceChargeWCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Capacity) {
          charges.CapacityChargeW.push(band.BandRate);
          charges.OldCapacityChargeW.push(band.OldRate??0);
          charges.CapacityChargeWCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Reactive) {
          charges.ReactiveChargeW.push(band.BandRate);
          charges.OldReactiveChargeW.push(band.OldRate??0);
          charges.ReactiveChargeWCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Legacy) {
          charges.LegacyChargeW.push(band.BandRate);
          charges.OldLegacyChargeW.push(band.OldRate??0);
          charges.LegacyChargeWCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Generation) {
          charges.GenerationChargeW.push(band.BandRate);
          charges.OldGenerationChargeW.push(band.OldRate??0);
          charges.GenerationChargeWCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Electrification) {
          charges.ElectrificationChargeW.push(band.BandRate);
          charges.OldElectrificationChargeW.push(band.OldRate??0);
          charges.ElectrificationChargeWCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Affordability) {
          charges.AffordabilityChargeW.push(band.BandRate);
          charges.OldAffordabilityChargeW.push(band.OldRate??0);
          charges.AffordabilityChargeWCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Ancillary) {
          charges.AncillaryChargeW.push(band.BandRate);
          charges.OldAncillaryChargeW.push(band.OldRate??0);
          charges.AncillaryChargeWCP.push(band.RateChangePercent??0);
        }

        else if (band.Name === ChargeName.CreditRev) {
          charges.CreditReverseChargeW.push(band.BandRate);
          charges.OldCreditReverseChargeW.push(band.OldRate??0);
          charges.CreditReverseChargeWCP.push(band.RateChangePercent??0);
        }
        else if(tariff.IsTimeOfUse){
          
           console.log('band.PSO', band.PSO)
          if (band.PSO === PSOShort.Peak) {
            charges.PeakW.push(band.BandRate);
           console.log('new PeakW', charges.PeakW)
            charges.OldPeakW.push(band.OldRate??0);
            charges.PeakWCP.push(band.RateChangePercent??0);
          }
          if (band.PSO === PSOShort.Standard) {
            charges.StandardW.push(band.BandRate);
           console.log('new StandardW', charges.StandardW)
            charges.OldStandardW.push(band.OldRate??0);
            charges.StandardWCP.push(band.RateChangePercent??0);
          }
          if (band.PSO === PSOShort.OffPeak) {
            charges.OffPeakW.push(band.BandRate);
           console.log('new OffPeakW', charges.OffPeakW)
            charges.OldOffPeakW.push(band.OldRate??0);
            charges.OffPeakWCP.push(band.RateChangePercent??0);
          }
        }
      }

      if (isSummer || isNone) {
        if (band.Name === ChargeName.Basic) {
          charges.BasicChargeS.push(band.BandRate);
          charges.OldBasicChargeS.push(band.OldRate??0);
          charges.BasicChargeSCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Demand) {
          charges.DemandChargeS.push(band.BandRate);
          charges.OldDemandChargeS.push(band.OldRate??0);
          charges.DemandChargeSCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Access) {
          charges.AccessChargeS.push(band.BandRate);
          charges.OldAccessChargeS.push(band.OldRate??0);
          charges.AccessChargeSCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Service) {
          charges.ServiceChargeS.push(band.BandRate);
          charges.OldServiceChargeS.push(band.OldRate??0);
          charges.ServiceChargeSCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Capacity) {
          charges.CapacityChargeS.push(band.BandRate);
          charges.OldCapacityChargeS.push(band.OldRate??0);
          charges.CapacityChargeSCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Reactive) {
          charges.ReactiveChargeS.push(band.BandRate);
          charges.OldReactiveChargeS.push(band.OldRate??0);
          charges.ReactiveChargeSCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Legacy) {
          charges.LegacyChargeS.push(band.BandRate);
          charges.OldLegacyChargeS.push(band.OldRate??0);
          charges.LegacyChargeSCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Generation) {
          charges.GenerationChargeS.push(band.BandRate);
          charges.OldGenerationChargeS.push(band.OldRate??0);
          charges.GenerationChargeSCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Electrification) {
          charges.ElectrificationChargeS.push(band.BandRate);
          charges.OldElectrificationChargeS.push(band.OldRate??0);
          charges.ElectrificationChargeSCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Affordability) {
          charges.AffordabilityChargeS.push(band.BandRate);
          charges.OldAffordabilityChargeS.push(band.OldRate??0);
          charges.AffordabilityChargeSCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.Ancillary) {
          charges.AncillaryChargeS.push(band.BandRate);
          charges.OldAncillaryChargeS.push(band.OldRate??0);
          charges.AncillaryChargeSCP.push(band.RateChangePercent??0);
        }
        else if (band.Name === ChargeName.CreditRev) {
          charges.CreditReverseChargeS.push(band.BandRate);
          charges.OldCreditReverseChargeS.push(band.OldRate??0);
          charges.CreditReverseChargeSCP.push(band.RateChangePercent??0);
        }
        else if(tariff.IsTimeOfUse){
          if (band.PSO === PSOShort.Peak) {
            charges.PeakS.push(band.BandRate);
            charges.OldPeakS.push(band.OldRate??0);
            charges.PeakSCP.push(band.RateChangePercent??0);
          }
          if (band.PSO === PSOShort.Standard) {
            charges.StandardS.push(band.BandRate);
            charges.OldStandardS.push(band.OldRate??0);
            charges.StandardSCP.push(band.RateChangePercent??0);
          }
          if (band.PSO === PSOShort.OffPeak) {
            charges.OffPeakS.push(band.BandRate);
            charges.OldOffPeakS.push(band.OldRate??0);
            charges.OffPeakSCP.push(band.RateChangePercent??0);
          }
        }
      }

      charges.IsActive.push( band.IsActive)
      charges.BandId.push( band.Id)
      charges.YearOfTariff = band.YearOfTariff
    }

    return {
      ...base,
      ...charges,
    };
  });
  return res;
}
  const tableRows = ref<any[]>([])
  const buildTableRows = () => {
  const rows: any[] = []

  for (const record of flatData.value) {
    const maxRows = Math.max(
      record.Block.length,
      record.Rate.length,
      record.PeakW.length,
      record.StandardW.length,
      record.OffPeakW.length,
      record.PeakS.length,
      record.StandardS.length,
      record.OffPeakS.length,
      record.BasicChargeW.length,
      record.DemandChargeW.length,
      record.AccessChargeW.length,
      record.ServiceChargeW.length,
      record.CapacityChargeW.length,
      record.ReactiveChargeW.length,
      record.LegacyChargeW.length,
      record.GenerationChargeW.length,
      record.CreditReverseChargeW.length,
      record.BasicChargeS.length,
      record.DemandChargeS.length,
      record.AccessChargeS.length,
      record.ServiceChargeS.length,
      record.CapacityChargeS.length,
      record.ReactiveChargeS.length,
      record.GenerationChargeS.length,
      record.LegacyChargeS.length,
      record.CreditReverseChargeS.length,
    )

    for (let i = 0; i < maxRows; i++) {
      rows.push({
        ...record,
        Block: record.Block[i] ?? null,
        Rate: record.Rate[i] ?? null,
        OldRate: record.OldRate[i] ?? null,
        RateCP: record.RateCP[i] ?? null,

        PeakW: record.PeakW[i] ?? null,
        OldPeakW: record.OldPeakW[i] ?? null,
        PeakWCP: record.PeakWCP[i] ?? null,

        StandardW: record.StandardW[i] ?? null,
        OldStandardW: record.OldStandardW[i] ?? null,
        StandardWCP: record.StandardWCP[i] ?? null,

        OffPeakW: record.OffPeakW[i] ?? null,
        OldOffPeakW: record.OldOffPeakW[i] ?? null,
        OffPeakWCP: record.OffPeakWCP[i] ?? null,

        PeakS: record.PeakS[i] ?? null,
        OldPeakS: record.OldPeakS[i] ?? null,
        PeakSCP: record.PeakSCP[i] ?? null,

        StandardS: record.StandardS[i] ?? null,
        OldStandardS: record.OldStandardS[i] ?? null,
        StandardSCP: record.StandardSCP[i] ?? null,

        OffPeakS: record.OffPeakS[i] ?? null,
        OldOffPeakS: record.OldOffPeakS[i] ?? null,
        OffPeakSCP: record.OffPeakSCP[i] ?? null,

        BasicChargeW: record.BasicChargeW[i] ?? null,
        OldBasicChargeW: record.OldBasicChargeW[i] ?? null,
        BasicChargeWCP: record.BasicChargeWCP[i] ?? null,

        DemandChargeW: record.DemandChargeW[i] ?? null,
        OldDemandChargeW: record.OldDemandChargeW[i] ?? null,
        DemandChargeWCP: record.DemandChargeWCP[i] ?? null,

        AccessChargeW: record.AccessChargeW[i] ?? null,
        OldAccessChargeW: record.OldAccessChargeW[i] ?? null,
        AccessChargeWCP: record.AccessChargeWCP[i] ?? null,

        ServiceChargeW: record.ServiceChargeW[i] ?? null,
        OldServiceChargeW: record.OldServiceChargeW[i] ?? null,
        ServiceChargeWCP: record.ServiceChargeWCP[i] ?? null,

        CapacityChargeW: record.CapacityChargeW[i] ?? null,
        OldCapacityChargeW: record.OldCapacityChargeW[i] ?? null,
        CapacityChargeWCP: record.CapacityChargeWCP[i] ?? null,

        ReactiveChargeW: record.ReactiveChargeW[i] ?? null,
        OldReactiveChargeW: record.OldReactiveChargeW[i] ?? null,
        ReactiveChargeWCP: record.ReactiveChargeWCP[i] ?? null,

        LegacyChargeW: record.LegacyChargeW[i] ?? null,
        OldLegacyChargeW: record.OldLegacyChargeW[i] ?? null,
        LegacyChargeWCP: record.LegacyChargeWCP[i] ?? null,

        ElectrificationChargeW: record.ElectrificationChargeW[i] ?? null,
        OldElectrificationChargeW: record.OldElectrificationChargeW[i] ?? null,
        ElectrificationChargeWCP: record.ElectrificationChargeWCP[i] ?? null, 

        AffordabilityChargeW: record.AffordabilityChargeW[i] ?? null,
        OldAffordabilityChargeW: record.OldAffordabilityChargeW[i] ?? null,
        AffordabilityChargeWCP: record.AffordabilityChargeWCP[i] ?? null,
        
        AncillaryChargeW: record.AncillaryChargeW[i] ?? null,
        OldAncillaryChargeW: record.OldAncillaryChargeW[i] ?? null,
        AncillaryChargeWCP: record.AncillaryChargeWCP[i] ?? null,

        GenerationChargeW: record.GenerationChargeW[i] ?? null,
        OldGenerationChargeW: record.OldGenerationChargeW[i] ?? null,
        GenerationChargeWCP: record.GenerationChargeWCP[i] ?? null,

        CreditReverseChargeW: record.CreditReverseChargeW[i] ?? null,
        OldCreditReverseChargeW: record.OldCreditReverseChargeW[i] ?? null,
        CreditReverseChargeWCP: record.CreditReverseChargeWCP[i] ?? null,

        BasicChargeS: record.BasicChargeS[i] ?? null,
        OldBasicChargeS: record.OldBasicChargeS[i] ?? null,
        BasicChargeSCP: record.BasicChargeSCP[i] ?? null,

        DemandChargeS: record.DemandChargeS[i] ?? null,
        OldDemandChargeS: record.OldDemandChargeS[i] ?? null,
        DemandChargeSCP: record.DemandChargeSCP[i] ?? null,

        AccessChargeS: record.AccessChargeS[i] ?? null,
        OldAccessChargeS: record.OldAccessChargeS[i] ?? null,
        AccessChargeSCP: record.AccessChargeSCP[i] ?? null,

        ServiceChargeS: record.ServiceChargeS[i] ?? null,
        OldServiceChargeS: record.OldServiceChargeS[i] ?? null,
        ServiceChargeSCP: record.ServiceChargeSCP[i] ?? null,

        CapacityChargeS: record.CapacityChargeS[i] ?? null,
        OldCapacityChargeS: record.OldCapacityChargeS[i] ?? null,
        CapacityChargeSCP: record.CapacityChargeSCP[i] ?? null,

        ReactiveChargeS: record.ReactiveChargeS[i] ?? null,
        OldReactiveChargeS: record.OldReactiveChargeS[i] ?? null,
        ReactiveChargeSCP: record.ReactiveChargeSCP[i] ?? null,

        LegacyChargeS: record.LegacyChargeS[i] ?? null,
        OldLegacyChargeS: record.OldLegacyChargeS[i] ?? null,
        LegacyChargeSCP: record.LegacyChargeSCP[i] ?? null,
        
        GenerationChargeS: record.GenerationChargeS[i] ?? null,
        OldGenerationChargeS: record.OldGenerationChargeS[i] ?? null,
        GenerationChargeSCP: record.GenerationChargeSCP[i] ?? null,

        ElectrificationChargeS: record.ElectrificationChargeS[i] ?? null,
        OldElectrificationChargeS: record.OldElectrificationChargeS[i] ?? null,
        ElectrificationChargeSCP: record.ElectrificationChargeSCP[i] ?? null, 

        AffordabilityChargeS: record.AffordabilityChargeS[i] ?? null,
        OldAffordabilityChargeS: record.OldAffordabilityChargeS[i] ?? null,
        AffordabilityChargeSCP: record.AffordabilityChargeSCP[i] ?? null,

        AncillaryChargeS: record.AncillaryChargeS[i] ?? null,
        OldAncillaryChargeS: record.OldAncillaryChargeS[i] ?? null,
        AncillaryChargeSCP: record.AncillaryChargeSCP[i] ?? null,

        CreditReverseChargeS: record.CreditReverseChargeS[i] ?? null,
        OldCreditReverseChargeS: record.OldCreditReverseChargeS[i] ?? null,
        CreditReverseChargeSCP: record.CreditReverseChargeSCP[i] ?? null,

        IsActive: record.IsActive[i],
        BandId: record.BandId[i],
      })
    }
  }

  tableRows.value = rows
}

watch(flatData, () => {
  buildTableRows()
}, { immediate: true })

const $selectAll = ref<HTMLInputElement>()
const selectedRecords = ref<number[]>([])

function isSelected(id: number) {
  const index = selectedRecords.value.indexOf(id)
  return index !== -1
}

function toggleSelection(e: Event, id: number) {
  const isCurrentlySelected = selectedRecords.value.includes(id)

  if (isCurrentlySelected) {
    const i = selectedRecords.value.indexOf(id)
    if (i !== -1) selectedRecords.value.splice(i, 1)
  } else {
    selectedRecords.value.push(id)
  }

  // Sync checkbox state
  if (e.target instanceof HTMLInputElement) {
    e.target.checked = !isCurrentlySelected
  }
}

function toggleAll(event: MouseEvent) {
  const target = event.target as HTMLInputElement

  if (target.checked) {
    selectedRecords.value =tableRows.value.map(x => x.TariffId)
  } else {
    selectedRecords.value = []
  }
}

watch(
  selectedYear,
  (newYears) => {
    if (newYears.length > 0) {
      selectedRecords.value = [];
    }
  },
  { immediate: true }
);

const downloadCsv = () => {

    let  records = tableRows.value
   if (selectedRecords.value.length > 0) {
      records = records.filter(record => selectedRecords.value.includes(record.TariffId));
    }
    if (!records || !records.length) {
      console.warn('No data to download.')
      return
    }

    const filename = !tariffNamesData.value? 'compare' : tariffNamesData.value
    .filter(tariff => tariffIds.value.includes(tariff.Id))
    .map(tariff => tariff.Name)
    .join('-').replace(/[^a-z0-9-_]/gi, '_').slice(0, 100) + '-' + Date.now().toString() + '.csv'
    
    const headers = Object.keys(records[0])

    const csvRows = [
      headers.join(','),
      ...records.map(record =>
        headers.map(field => JSON.stringify(record[field] ?? '')).join(',')
      )
    ]

    const csvContent = csvRows.join('\r\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
  const openTOU = ()=>{
    touProfiles.open()
  }
  const approveTariffs = async ()=>{
    try {
      await mutateAsync(selectedRecords)
      selectedRecords.value = [];
      addSuccess({
        title: 'Tariffs approved sucessfully.'
      })

    } catch {
      addError({
        title: 'Approval Failed.'
      })
    }

  }

  const hasChanges = computed(() => selectedRecords.value.length > 0)

</script>

<template>
<PageHeader>
    <div>
      <div class="d-inline-block text-nowrap">
        <label class="me-2">Region</label>
        <InputSelect
          name="region"
          :items="regionData??[]"
          multiple
          item-value="Id"
          item-text="Name"
          v-model:selected="regions"
          :display-limit="1"
          inline />
      </div>

      <div class="d-inline-block text-nowrap  ms-4">
        <label class="me-2">Utility Provider</label>
        <InputSelect
          name="utilityProvider"
          :items="utilityProviderData??[]"
          multiple
          item-value="Id"
          item-text="Name"
          v-model:selected="utilityProviders"
          :display-limit="1"
          inline />
      </div>

      <div class="d-inline-block text-nowrap ms-4">
        <label class="me-2">Tariff</label>
        <InputSelect
          :searchable="true"
          name="tariff"
          :items="tariffNamesData??[]"
          multiple
          item-value="Id"
          item-text="Name"
          v-model:selected="tariffIds"
          :display-limit="1"
          inline />
      </div>
    </div>
    <div>
      <ButtonSecondary
                :icon="faPenToSquare"
                @click="openTOU"
                >TOU Profile</ButtonSecondary>
    </div>
  </PageHeader>
  <PageContent>
    <VerticallyCentered v-if="flatData.length === 0">
      <HeroSplash
        title="Customize Your View!"
        sub-title="Select options from the dropdowns to populate the screen."
        :image-url="splashImage"
      />
    </VerticallyCentered>
    <Tabs v-else
      :tabs="years"
      v-model="selectedYear"
    />
    <PageContent class="d-flex flex-column h-100">
      <ContentLoader :loading="isFetching|| isPending" :variant="flatData.length === 0 ? 'overlay' : 'table'">
       
    <div v-if="IsCompare && flatData.length > 0" class="mb-3">
       <div class="d-flex align-items-center gap-2 mb-2 ms-4">
                <FontAwesomeIcon :icon="faCircleInfo" class="fa-2x text-yellow" />
                <span>Please note that these filters only apply to the Compare & Approve tab:</span>
            </div>
      <div class="d-inline-block text-nowrap  ms-4">
        <label class="me-2">Utility Provider</label>
        <InputSelect
          name="compareUtilityProvider"
          :items="compareUtilityProviderData??[]"
          multiple
          item-value="Name"
          item-text="Name"
          v-model:selected="compareUtilityProviders"
          :display-limit="1"
          inline />
      </div>

      <div class="d-inline-block text-nowrap ms-4">
        <label class="me-2">Tariff</label>
        <InputSelect
          :searchable="true"
          name="tariff"
          :items="compareTariffNamesData??[]"
          multiple
          item-value="Name"
          item-text="Name"
          v-model:selected="comparetariffNames"
          :display-limit="1"
          inline />
      </div>
    </div>
        <div class="table-responsive" v-if="flatData.length > 0">
          <table class="table w-100 table-hover u-data-table table-sm">
            <thead>
              <!-- Top Header Row -->
              <tr class="no-bottom-border">
                <th :colspan="IsCompare? 8:7" class="border-right"></th>
                <th :colspan="IsCompare? 6:3" class="text-center border-right">High Demand</th>
                <th :colspan="IsCompare? 6:3" class="text-center border-right">Low Demand</th>
                <th :colspan="IsCompare? 18:9" class="text-center border-right">High Demand</th>
                <th :colspan="IsCompare? 18:9" class="text-center border-right">Low Demand</th>
              </tr>

              <!-- Column Headers -->
              <tr class="text-nowrap">
                <th>
                  <input type="checkbox" class="form-check-input" ref="$selectAll" @click="toggleAll" v-tooltip="selectedRecords.length > 0 ? `${selectedRecords.length} selected` : undefined" />
                </th>
                <th>Utility Provider</th>
                <th>Tariff</th>
                <th>Trans_Zone</th>
                <th>Voltage</th>
                <th>Block</th>
                <th v-if="IsCompare">Rate<br>Old</th>
                <th class="border-right">Rate<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Peak<br>Old</th>
                <th>Peak<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Standard<br>Old</th>
                <th>Standard<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Off-Peak<br>Old</th>
                <th class="border-right">Off-Peak<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Peak<br>Old</th>
                <th>Peak<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Standard<br>Old</th>
                <th>Standard<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Off-Peak<br>Old</th>
                <th class="border-right">Off-Peak<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Basic Charge<br>Old</th>
                <th>Basic Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Demand Charge<br>Old</th>
                <th>Demand Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Access Charge<br>Old</th>
                <th>Access Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Service Charge<br>Old</th>
                <th>Service Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Capacity Charge<br>Old</th>
                <th>Capacity Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Reactive Charge<br>Old</th>
                <th>Reactive Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Legacy Charge<br>Old</th>
                <th>Legacy Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Generation Charge<br>Old</th>
                <th>Generation Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Generation Charge<br>Old</th>
                <th>Electrification Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Electrification Charge<br>Old</th>
                <th>Affordability Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Affordability Charge<br>Old</th>
                <th>Ancillary Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Ancillary Charge<br>Old</th>
                <th v-if="IsCompare">Credit Reverse Charge<br>Old</th>
                <th class="border-right">Credit Reverse Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Basic Charge<br>Old</th>
                <th>Basic Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Demand Charge<br>Old</th>
                <th>Demand Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Access Charge<br>Old</th>
                <th>Access Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Service Charge<br>Old</th>
                <th>Service Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Capacity Charge<br>Old</th>
                <th>Capacity Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Reactive Charge<br>Old</th>
                <th>Reactive Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Legacy Charge<br>Old</th>
                <th>Legacy Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Generation Charge<br>Old</th>
                <th>Generation Charge<span v-if="IsCompare"><br>New</span></th>
                <th>Electrification Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Electrification Charge<br>Old</th>
                <th>Affordability Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Affordability Charge<br>Old</th>
                <th>Ancillary Charge<span v-if="IsCompare"><br>New</span></th>
                <th v-if="IsCompare">Ancillary Charge<br>Old</th>
                <th v-if="IsCompare">Credit Reverse<br>Old</th>
                <th class="border-right">Credit Reverse Charge<span v-if="IsCompare"><br>New</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(record, index) in tableRows" :key="index" class="text-nowrap">
                <td>
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :checked="isSelected(record.TariffId)"
                    @change="toggleSelection($event, record.TariffId)" />
                </td>
                <td>{{ record.ProviderName }}</td>
                <td>{{ record.TariffName }}</td>
                <td>{{ record.TransZone }}</td>
                <td>{{ record.Voltage }}</td>
                <td>{{ record.Block }}</td>
                <td v-if="IsCompare">{{ record.OldRate }}</td>
                <td class="border-right" :class="{'text-red': IsCompare && record.RateCP > 15, 'text-green': IsCompare && record.RateCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.RateCP + '%'} : undefined">{{ record.Rate }}</td>

                <!-- Winter Energy Charges (W = Winter) -->
                <td class="text-center" v-if="IsCompare">{{ record.OldPeakW }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.PeakWCP > 15, 'text-green': IsCompare && record.PeakWCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.PeakWCP + '%'} : undefined">{{ record.PeakW }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldStandardW }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.StandardWCP > 15, 'text-green': IsCompare && record.StandardWCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.StandardWCP + '%'} : undefined">{{ record.StandardW }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldOffPeakW }}</td>
                <td class="text-center border-right" :class="{'text-red': IsCompare && record.OffPeakWCP > 15, 'text-green': IsCompare && record.OffPeakWCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.OffPeakWCP + '%'} : undefined">{{ record.OffPeakW }}</td>

                <!-- Summer Energy Charges (S = Summer) -->
                <td class="text-center" v-if="IsCompare">{{ record.OldPeakS }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.PeakSCP > 15, 'text-green': IsCompare && record.PeakSCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.PeakSCP + '%'} : undefined">{{ record.PeakS }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldStandardS }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.StandardSCP > 15, 'text-green': IsCompare && record.StandardSCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.StandardSCP + '%'} : undefined">{{ record.StandardS }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldOffPeakS }}</td>
                <td class="text-center border-right" :class="{'text-red': IsCompare && record.OffPeakSCP > 15, 'text-green': IsCompare && record.OffPeakSCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.OffPeakSCP + '%'} : undefined">{{ record.OffPeakS }}</td>

                <!-- Winter or None Charges -->
                <td class="text-center" v-if="IsCompare">{{ record.OldBasicChargeW }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.BasicChargeWCP > 15, 'text-green': IsCompare && record.BasicChargeWCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.BasicChargeWCP + '%'} : undefined">{{ record.BasicChargeW }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldDemandChargeW }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.DemandChargeWCP > 15, 'text-green': IsCompare && record.DemandChargeWCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.DemandChargeWCP + '%'} : undefined">{{ record.DemandChargeW }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldAccessChargeW }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.AccessChargeWCP > 15, 'text-green': IsCompare && record.AccessChargeWCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.AccessChargeWCP + '%'} : undefined">{{ record.AccessChargeW }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldServiceChargeW }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.ServiceChargeWCP > 15, 'text-green': IsCompare && record.ServiceChargeWCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.ServiceChargeWCP + '%'} : undefined">{{ record.ServiceChargeW }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldCapacityChargeW }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.CapacityChargeWCP > 15, 'text-green': IsCompare && record.CapacityChargeWCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.CapacityChargeWCP + '%'} : undefined">{{ record.CapacityChargeW }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldReactiveChargeW }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.ReactiveChargeWCP > 15, 'text-green': IsCompare && record.ReactiveChargeWCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.ReactiveChargeWCP + '%'} : undefined">{{ record.ReactiveChargeW }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldLegacyChargeW }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.LegacyChargeWCP > 15, 'text-green': IsCompare && record.LegacyChargeWCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.LegacyChargeWCP + '%'} : undefined">{{ record.LegacyChargeW }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldGenerationChargeW}}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.GenerationChargeWCP > 15, 'text-green': IsCompare && record.GenerationChargeWCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.GenerationChargeWCP + '%'} : undefined">{{ record.GenerationChargeW }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldElectrificationChargeW}}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.ElectrificationChargeWCP > 15, 'text-green': IsCompare && record.ElectrificationChargeWCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.ElectrificationChargeWCP + '%'} : undefined">{{ record.ElectrificationChargeW }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldAffordabilityChargeW}}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.AffordabilityChargeWCP > 15, 'text-green': IsCompare && record.AffordabilityChargeWCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.AffordabilityChargeWCP + '%'} : undefined">{{ record.AffordabilityChargeW }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldAncillaryChargeW}}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.AncillaryChargeWCP > 15, 'text-green': IsCompare && record.AncillaryChargeWCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.AncillaryChargeWCP + '%'} : undefined">{{ record.AncillaryChargeW }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldCreditReverseChargeW }}</td>
                <td class="text-center border-right" :class="{'text-red': IsCompare && record.CreditReverseChargeWCP > 15, 'text-green': IsCompare && record.CreditReverseChargeWCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.CreditReverseChargeWCP + '%'} : undefined">{{ record.CreditReverseChargeW }}</td>

                <!-- Summer or None Charges -->
                <td class="text-center" v-if="IsCompare">{{ record.OldBasicChargeS }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.BasicChargeSCP > 15, 'text-green': IsCompare && record.BasicChargeSCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.BasicChargeSCP + '%'} : undefined">{{ record.BasicChargeS }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldDemandChargeS }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.DemandChargeSCP > 15, 'text-green': IsCompare && record.DemandChargeSCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.DemandChargeSCP + '%'} : undefined">{{ record.DemandChargeS }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldAccessChargeS }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.AccessChargeSCP > 15, 'text-green': IsCompare && record.AccessChargeSCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.AccessChargeSCP + '%'} : undefined">{{ record.AccessChargeS }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldServiceChargeS }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.ServiceChargeSCP > 15, 'text-green': IsCompare && record.ServiceChargeSCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.ServiceChargeSCP + '%'} : undefined">{{ record.ServiceChargeS }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldCapacityChargeS }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.CapacityChargeSCP > 15, 'text-green': IsCompare && record.CapacityChargeSCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.CapacityChargeSCP + '%'} : undefined">{{ record.CapacityChargeS }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldReactiveChargeS }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.ReactiveChargeSCP > 15, 'text-green': IsCompare && record.ReactiveChargeSCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.ReactiveChargeSCP + '%'} : undefined">{{ record.ReactiveChargeS }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldLegacyChargeS }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.LegacyChargeSCP > 15, 'text-green': IsCompare && record.LegacyChargeSCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.LegacyChargeSCP + '%'} : undefined">{{ record.LegacyChargeS }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldGenerationChargeS }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.GenerationChargeSCP > 15, 'text-green': IsCompare && record.GenerationChargeSCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.GenerationChargeSCP + '%'} : undefined">{{ record.GenerationChargeS }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldElectrificationChargeS}}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.ElectrificationChargeSCP > 15, 'text-green': IsCompare && record.ElectrificationChargeSCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.ElectrificationChargeSCP + '%'} : undefined">{{ record.ElectrificationChargeS }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldAffordabilityChargeS}}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.AffordabilityChargeSCP > 15, 'text-green': IsCompare && record.AffordabilityChargeSCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.AffordabilityChargeSCP + '%'} : undefined">{{ record.AffordabilityChargeS }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldAncillaryChargeS}}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.AncillaryChargeSCP > 15, 'text-green': IsCompare && record.AncillaryChargeSCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.AncillaryChargeSCP + '%'} : undefined">{{ record.AncillaryChargeS }}</td>
                <td class="text-center" v-if="IsCompare">{{ record.OldCreditReverseChargeS }}</td>
                <td class="text-center" :class="{'text-red': IsCompare && record.CreditReverseChargeSCP > 15, 'text-green': IsCompare && record.CreditReverseChargeSCP < 15}" v-tooltip="IsCompare ?{ placement: 'bottom', text: 'Increase:' + record.CreditReverseChargeSCP + '%'} : undefined">{{ record.CreditReverseChargeS }}</td>
              </tr>

              <!-- Empty State -->
              <tr v-if="flatData.length === 0">
                <td colspan="27" class="text-center text-muted">We couldn't find any tariffs that match your search.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </ContentLoader>
      <div class="mt-4 py-3 px-4 bg-white"
          style="position: sticky; bottom: 0; z-index: 10;"
          v-if="flatData.length > 0">
        <ButtonSecondary
                  class="me-3"
                  :icon="faDownload"
                  @click="downloadCsv"
                  :disabled="!hasChanges"
                  >Download</ButtonSecondary>
        <ButtonHighlighted
                v-if="selectedYear.startsWith('Compare')"
                :icon="faHandPointUp"
                @click="approveTariffs"
                >Approve ({{selectedRecords.length}})</ButtonHighlighted>
    </div>
    </PageContent>
    <ModalTouProfile
      v-if="touProfiles.isOpen"
      @close="touProfiles.close()" />
  </PageContent>
</template>

<style lang="scss" scoped>
  .no-bottom-border th {
    border-bottom: none;
  }
  .border-right {
    border-right: 1px solid #D1D1D1
  }
  .text-red {
    color: #FD5624
  }
  .text-green {
    color: #28A09C
  }
</style>
